import store from "@/store";

/**
 * @description คันเร่ง
 * @param fn { Function } วิธี
 * @param time { Number } ช่วงเวลา
 * @return { Function } วิธีสุดท้ายในการควบคุมปริมาณ
 */
export const throttle = (
    fn: (...params: any[]) => void,
    time = 1000
) => {
    let previousTime = new Date(0).getTime();
    return (...args: []) => {
        let nowTime = new Date().getTime();
        if (nowTime - previousTime > time) {
            previousTime = nowTime;
            return fn(args);
        }
    }
}


/**
 * @description ป้องกันการสั่นไหว
 * @param fn { Function } วิธี
 * @param time { Number } ช่วงเวลา
 * @return { Function } วิธีสุดท้ายของการประมวลผลป้องกันภาพสั่นไหว
 */
export const debounce = (
    fn: (...params: any[]) => void,
    time = 1000
): any => {
    let timer: any = null;
    return (...args: []) => {
        if (timer) clearTimeout(timer);

        timer = setTimeout(() => {
            timer = null;
            fn(args);
        }, time);
    }
}


/**
 * @description อาร์เรย์แบน
 * @param arr { Array } วัตถุแบน
 * @return { Array } อาร์เรย์ที่แบน
 */
export const flatten = (arr: any[]): any[] => {
    return arr.reduce((result, item) => {
        return result.concat(Array.isArray(item) ? flatten(item) : item);
    }, []);
}


/**
 * @description การร้องขอการแบ่งหน้า
 * @param [page] { Number } ขอหมายเลขหน้า
 * @param [size] { Number } จำนวนหน้าเดียวที่ร้องขอ
 * @return { Object } อินสแตนซ์ของคลาสนี้
 */
export class RequestPaging {
    private _page: number = 1;
    private _size: number = 10;
    private _loading: boolean = false;
    private _count: number = 0
    private _lists: any[] = []
    constructor(payload?: { page?: number, size?: number }) {
        if (payload?.page) this._page = payload.page;
        if (payload?.size) this._size = payload.size;
    }

    // ขอข้อมูล
    request(payload: { callback: any, params?: object }): Promise<any> {
        // ปิดการใช้งานคำขอที่เกิดขึ้นพร้อมกัน
        if (this._loading) return Promise.reject();
        else this._loading = true;

        return payload.callback({
            page_no: this._page,
            page_size: this._size,
            ...payload.params
        }).then((res: any) => {
            this._count = res?.count
            this._lists = res?.lists
            return Promise.resolve(res)
        }).catch((err: any) => {
            return Promise.reject(err);
        }).finally(() => {
            this._loading = false;
        });
    }

    get page(): number {
        return this._page;
    }

    get size(): number {
        return this._size;
    }

    get loading(): boolean {
        return this._loading;
    }
    set loading(loading: boolean) {
        this._loading = loading
    }
    set page(num: number) {
        this._page = num;
    }

    set size(num: number) {
        this._size = num;
    }

    get count() {
        return this._count
    }

    get lists() {
        return this._lists
    }
}


/**
 * @description รับสตริงสุ่ม
 * @param length { Number } รับจำนวนหลัก
 * @return { String } สตริงสุ่ม
 */
export const getRandomString = (length: number): string => {
    const dictionaries = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'
    let result = ''

    for (let i = length; i > 0; --i) {
        const index = Math.floor(Math.random() * dictionaries.length)
        result += dictionaries[index]
    }

    return result
}


/**
 * @description รับเอกลักษณ์id
 * @param length { Number } idความยาว
 * @return { String } id
 */
export const getNonDuplicateID = (length: number = 8) => {
    let idStr = Date.now().toString(36)
    idStr += Math.random().toString(36).substr(3, length)
    return idStr
}


/**
 * @description คัดลอกไปยังคลิปบอร์ด
 * @param value { String } คัดลอกเนื้อหา
 * @return { Promise } resolve | reject
 */
export const copyClipboard = (value: string) => {
    const elInput = document.createElement('input')

    elInput.setAttribute('value', value)
    document.body.appendChild(elInput)
    elInput.select()

    try {
        if (document.execCommand('copy'))
            return Promise.resolve()
        else
            throw new Error()
    } catch (err) {
        return Promise.reject(err)
    } finally {
        document.body.removeChild(elInput)
    }
}


/**
 * @description การจัดรูปแบบเวลา
 * @param dateTime { number } ผิดเวลา
 * @param fmt { string } รูปแบบเวลา
 * @return { string }
 */
// yyyy:mm:dd|yyyy:mm|yyyyปีmmวันddวัน|yyyyปีmmวันddวัน hhชั่วโมงMMการให้เกรด,ชุดค่าผสมที่ปรับแต่งได้
export const timeFormat = (dateTime: number, fmt = 'yyyy-mm-dd') => {
    // ถ้าสำหรับnull,จากนั้นจัดรูปแบบเวลาปัจจุบัน
    if (!dateTime) dateTime = Number(new Date());
    // ถ้าdateTimeความยาวคือ10หรือ13，จากนั้นประทับเวลาเป็นวินาทีและมิลลิวินาที，ถ้า超过13นิดหน่อย，เป็นรูปแบบเวลาอื่น
    if (dateTime.toString().length == 10) dateTime *= 1000;
    let date = new Date(dateTime);
    let ret;
    let opt: any = {
        'y+': date.getFullYear().toString(), // ปี
        'm+': (date.getMonth() + 1).toString(), // วัน
        'd+': date.getDate().toString(), // วัน
        'h+': date.getHours().toString(), // ชั่วโมง
        'M+': date.getMinutes().toString(), // จุด
        's+': date.getSeconds().toString() // ที่สอง
    };
    for (let k in opt) {
        ret = new RegExp('(' + k + ')').exec(fmt);
        if (ret) {
            fmt = fmt.replace(ret[1], ret[1].length == 1 ? opt[k] : opt[k].padStart(ret[1].length, '0'));
        }
    }
    return fmt;
}


/**
 * @description ชื่อโดเมนเต็มของรูปภาพ
 * @param uri { string } ลิงค์รูปภาพ
 * @return { string }
 */
//
export const getImageUri = (uri: string = '') => {
    const oss_domain = store.state.app.config.oss_domain || ''
    return oss_domain + uri
}

export const objectToQuery = (data: any) => {
    let _result = []

    for (let key in data) {
        let value = data[key]
        if (value.constructor == Array) {
            value.forEach(_value => {
                _result.push(key + "=" + _value)
            })
        } else {
            _result.push(key + '=' + value)
        }
    }

    return _result.join('&')
}



/**
 * สำเนาลึก
 * @param {any} target ออบเจ็กต์ที่ต้องคัดลอกแบบลึก
 * @returns {Object}
 */
export function deepClone(target: any) {
    if (typeof target !== 'object' || target === null) {
        return target
    }

    const cloneResult: any = Array.isArray(target) ? [] : {}

    for (const key in target) {
        if (Object.prototype.hasOwnProperty.call(target, key)) {
            const value = target[key]

            if (typeof value === 'object' && value !== null) {
                cloneResult[key] = deepClone(value)
            } else {
                cloneResult[key] = value
            }
        }
    }

    return cloneResult
}
